import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Leaf, ArrowLeft, Scale, Shield, FileText, Mail } from "lucide-react";

export default function Legal() {
  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <Leaf className="w-6 h-6 text-primary-foreground" />
            </div>
            <span className="text-xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>VF Decide</span>
          </Link>
          <Link to="/">
            <Button variant="ghost">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
            <Scale className="w-4 h-4" />
            Informations Légales
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4" style={{ fontFamily: 'Manrope' }}>
            Mentions Légales
          </h1>
          <p className="text-lg text-muted-foreground">
            Transparence et conformité au service de nos utilisateurs
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-24 px-6">
        <div className="max-w-4xl mx-auto space-y-8">
          
          {/* Éditeur */}
          <Card className="border-0 shadow-md" data-testid="legal-editor">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <FileText className="w-5 h-5 text-primary" />
                Éditeur du Site
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <p><strong className="text-foreground">Raison sociale :</strong> VF ACTIONS SAS</p>
              <p><strong className="text-foreground">Forme juridique :</strong> Société par Actions Simplifiée</p>
              <p><strong className="text-foreground">Capital social :</strong> 10 000 €</p>
              <p><strong className="text-foreground">Siège social :</strong> 15 Avenue de la RSE, 75008 Paris, France</p>
              <p><strong className="text-foreground">RCS :</strong> Paris B 123 456 789</p>
              <p><strong className="text-foreground">N° TVA Intracommunautaire :</strong> FR 12 123456789</p>
              <p><strong className="text-foreground">Directeur de la publication :</strong> Le Président de VF ACTIONS SAS</p>
            </CardContent>
          </Card>

          {/* Hébergement */}
          <Card className="border-0 shadow-md" data-testid="legal-hosting">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <Shield className="w-5 h-5 text-primary" />
                Hébergement
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <p><strong className="text-foreground">Hébergeur :</strong> Emergent Labs Inc.</p>
              <p><strong className="text-foreground">Infrastructure :</strong> Cloud sécurisé avec chiffrement SSL/TLS</p>
              <p><strong className="text-foreground">Localisation des données :</strong> Union Européenne</p>
              <p><strong className="text-foreground">Certification :</strong> ISO 27001, SOC 2 Type II</p>
            </CardContent>
          </Card>

          {/* Propriété intellectuelle */}
          <Card className="border-0 shadow-md" data-testid="legal-ip">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <Scale className="w-5 h-5 text-primary" />
                Propriété Intellectuelle
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <p>
                L'ensemble des contenus présents sur le site VF Decide (textes, graphismes, logos, icônes, images, 
                clips audio et vidéo, bases de données, logiciels) est protégé par les dispositions du Code de la 
                propriété intellectuelle français et par les traités internationaux.
              </p>
              <p>
                La marque "VF Decide" et le logo associé sont des marques déposées. Toute reproduction, 
                représentation, modification ou exploitation non autorisée est interdite et constitue une 
                contrefaçon sanctionnée par les articles L.335-2 et suivants du Code de la propriété intellectuelle.
              </p>
              <p>
                VF Decide® et VF ACTIONS® sont des marques enregistrées auprès de l'INPI.
              </p>
            </CardContent>
          </Card>

          {/* Contact */}
          <Card className="border-0 shadow-md bg-primary/5" data-testid="legal-contact">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <Mail className="w-5 h-5 text-primary" />
                Contact
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <p>Pour toute question relative aux mentions légales, vous pouvez nous contacter :</p>
              <p><strong className="text-foreground">Email :</strong> legal@vf-decide.com</p>
              <p><strong className="text-foreground">Courrier :</strong> VF ACTIONS SAS - Service Juridique, 15 Avenue de la RSE, 75008 Paris</p>
            </CardContent>
          </Card>

          {/* Links to other legal pages */}
          <div className="flex flex-wrap justify-center gap-4 pt-8">
            <Link to="/cgu">
              <Button variant="outline" className="rounded-full">
                Conditions Générales d'Utilisation
              </Button>
            </Link>
            <Link to="/confidentialite">
              <Button variant="outline" className="rounded-full">
                Politique de Confidentialité
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-card border-t border-border">
        <div className="max-w-4xl mx-auto text-center text-sm text-muted-foreground">
          <p>© 2026 VF Decide - VF ACTIONS SAS. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
}
